import * as React from 'react'
import { styled, css } from 'next-yak'
import * as S from '../../../styles'

export type CheckboxComponentProps = {
  error?: any
} & React.InputHTMLAttributes<HTMLInputElement>

/** will forward any other props to the underlying `<input>` */
function CheckboxComponent({
  className,
  error,
  ...inputProps
}: CheckboxComponentProps): React.ReactElement<CheckboxComponentProps> {
  return (
    <CheckboxWrapper className={className}>
      <S.input.HiddenFocusableInput type="checkbox" {...inputProps} />
      <Container>
        <ControlledCheckboxComponent error={error} disabled={inputProps.disabled} />
      </Container>
    </CheckboxWrapper>
  )
}

export const Checkbox = styled(CheckboxComponent)<CheckboxComponentProps>``
Checkbox.displayName = 'Checkbox'

const DefaultCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: undefined })`
  display: inline-block;
`

const CheckedCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'checked' })`
  display: none;
`

const FocusedCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'focused' })`
  display: none;
`

const ErrorCheckmark = styled(S.checkbox.Checkmark).attrs({ $status: 'error' })`
  display: none;
`

export const ControlledCheckboxWrapper = styled.div<{ $isError: boolean }>`
  position: relative;
  width: 18px;
  height: 18px;
  ${({ $isError }) => {
    if ($isError) {
      return css`
        ${ErrorCheckmark} {
          display: inline-block;
        }
        ${DefaultCheckmark}, ${FocusedCheckmark}, ${CheckedCheckmark} {
          display: none;
        }
      `
    }
  }}
`

type ControlledCheckboxProps = {
  className?: string
  error?: any
  disabled?: boolean
}

function ControlledCheckboxComponent({
  className,
  error,
  disabled,
}: ControlledCheckboxProps): React.ReactElement<ControlledCheckboxProps> {
  return (
    <ControlledCheckboxWrapper className={className} $isError={Boolean(error)}>
      <DefaultCheckmark $disabled={disabled} />
      <FocusedCheckmark $disabled={disabled} />
      <CheckedCheckmark $disabled={disabled} />
      <ErrorCheckmark $disabled={disabled} />
    </ControlledCheckboxWrapper>
  )
}

export const ControlledCheckbox = styled(ControlledCheckboxComponent)``

const Container = styled.div`
  // yak does not give a class to non exported styled-component with no css.
  // this is an issue because this component is used as a selector later in the file
  // to fix this we need to add a bogus css rule to force yak to generate a class
  // see : https://github.com/DigitecGalaxus/next-yak/issues/373
  --bogus-style: 0;
`

export const focusedCheckboxCheck = css`
  ${FocusedCheckmark} {
    display: inline-block;
  }

  ${DefaultCheckmark}, ${CheckedCheckmark}, ${ErrorCheckmark} {
    display: none;
  }
`

export const checkedCheckboxCheck = css`
  ${CheckedCheckmark} {
    display: inline-block;
  }

  ${DefaultCheckmark}, ${FocusedCheckmark}, ${ErrorCheckmark} {
    display: none;
  }
`

const CheckboxWrapper = styled.div`
  ${S.input.HiddenFocusableInput}:focus + ${Container} {
    ${focusedCheckboxCheck};
  }

  ${S.input.HiddenFocusableInput}:checked + ${Container} {
    ${checkedCheckboxCheck};
  }
`
